package data.objects.views.cardinfo

import analytics.AnalyticsCategory
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.tags.getTagValues
import apiclient.tags.getUniqueTag
import apiclient.util.isNotNullOrEmpty
import auth.permissions.PermissionsService
import breadcrumbs.BreadcrumbsCache
import data.ObjectAndUserHandler
import data.objects.ActiveObjectStore
import data.objects.views.directediting.directEditableMarkerButton
import data.objects.views.directediting.directEditingCardContent
import data.objects.views.directediting.editable
import data.objects.views.directediting.meetingAttendeesButton
import data.objects.views.directediting.twMeetingDateTimeButton
import data.objects.views.directediting.twMeetingInvitationStatusButton
import data.objects.views.directediting.twTaskAssignmentButton
import data.objects.views.directediting.twTaskDueDateButton
import data.objects.views.directediting.twTaskStatusButton
import data.objects.views.iconNumber
import dev.fritz2.components.flexBox
import dev.fritz2.components.icon
import dev.fritz2.core.Id
import dev.fritz2.core.RenderContext
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.koinCtx
import koin.withKoin
import kotlin.math.max
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import localization.TL
import localization.getTranslationFlow
import localization.translate
import location.GeoPositionStore
import location.geolocation.latLon
import mainmenu.RouterStore
import mapsearch.getSvgIconOptions
import mapsearch.globalsearch.toMapMarkerResult
import model.L
import overlays.dotSpinner
import qrcode.ScannedCodeStore
import search.nestedObjects.NestedObjectsResultsStore
import search.separationLine
import search.titleSubtitleStack
import svgmarker.MarkerSize
import svgmarker.renderSvgIcon
import theme.FormationIcons
import theme.FormationUIIcons
import twcomponents.twColOf
import twcomponents.twColOfNoGap
import twcomponents.twIconCustomSize
import twcomponents.twIconMedium
import twcomponents.twIconSmall
import twcomponents.twLargeIconButtonNeutralRounded
import twcomponents.twMediumIconButtonNeutralRounded
import twcomponents.twPrimaryButton
import twcomponents.twRowOf
import twcomponents.twRowOfNoGap
import twcomponents.twSimpleTextTooltip
import twcomponents.twTooltip
import utils.getCase
import utils.getIcon
import utils.getName
import utils.merge
import webcomponents.cardTitleWithSubtitle
import webcomponents.cardTitleWithSubtitleDirectEdit
import webcomponents.ellipseText
import webcomponents.oneButtonFooter
import webcomponents.selectorButton
import webcomponents.selectorContent
import webcomponents.twEllipseText

fun RenderContext.cardInfo() {

    withKoin {
        val activeObjectStore: ActiveObjectStore = get()
        val routerStore: RouterStore = get()
        val title = activeObjectStore.map(GeoObjectDetails.L.title)
        val objectType = activeObjectStore.map(GeoObjectDetails.L.objectType)

        // for generalMarker
        val geoReferencedConnectableObject = activeObjectStore.map(GeoObjectDetails.L.geoReferencedConnectableObject)

        // for zone
        val customData = activeObjectStore.map(GeoObjectDetails.L.tags).data.map { it.getTagValues(ObjectTags.ExtraData) }
        val customDataFieldsMapFlow = getCustomFieldMap(activeObjectStore.map(GeoObjectDetails.L.tags).data.map { it.getTagValues(ObjectTags.CustomField) })
        val customDataZoneType = customDataFieldsMapFlow.map { fieldMap -> fieldMap["Type"]?.fieldValue }

        // for objectMarker
        val scannedCodeStore: ScannedCodeStore = get()
        val geoPositionStore: GeoPositionStore = get()
        val permissionsService: PermissionsService = get()
        val rawCustomData =
            activeObjectStore.map(GeoObjectDetails.L.tags).data.map { it.getTagValues(ObjectTags.ExtraData) }
        val ephemeral =
            activeObjectStore.map(GeoObjectDetails.L.tags).data.map { it.getUniqueTag(ObjectTags.Ephemeral).toBoolean() }


        div(
            "flex flex-col items-stretch justify-between w-full h-full",
            id = "card-layout-${Id.next()}",
        ) {
            activeObjectStore.data.map { it.editable() }.render { editAccess ->
                // Header
                breadCrumbs(activeObjectStore.current)
                div(
                    "flex flex-col items-stretch justify-between w-full p-2",
                    id = "card-layout-header-${Id.next()}",
                ) {
                    twRowOfNoGap("w-full h-max items-start justify-between") {
                        directEditableMarkerButton()
                        if (editAccess) {
                            cardTitleWithSubtitleDirectEdit(
                                titleFlow = title.data,
                                title = title.current,
                                subtitle = objectType.data.map { it.getName() },
                                changeHandler = activeObjectStore.editTitle,
                            )
                        } else {
                            cardTitleWithSubtitle(
                                title = title.data,
                                subtitle = objectType.data.map { it.getName() },
                            )
                        }
                        closeCardButton()
                    }
                }
                // Scrollable content
                directEditingCardContent {
                    when (objectType.current) {
                        ObjectType.ObjectMarker -> {
                            showCustomData(customDataFieldsMapFlow)
                            // OPTIONAL EXTRA DATA BUTTON
                            // TODO turn extra data screen into modal
                            rawCustomData.render { data ->
                                if (data.isNotEmpty()) {
                                    if (data.size < 4) {
                                        showExtraData(data)
                                    } else {
                                        selectorButton {
                                            selectorContent {
                                                icon({ margins { horizontal { smaller } }; size { normal } }) { fromTheme { FormationIcons.API.icon } }
                                                ellipseText { translate(TL.CardCustomData.BUTTON_TITLE) }
                                            }
                                            clicks.map {
                                                mapOf(
                                                    "show" to "objectData",
                                                )
                                            } handledBy routerStore.addOrReplaceRoute
                                        }
                                    }
                                }
                            }

                            ephemeral.render { isEphemeral ->
                                if (isEphemeral) {
                                    oneButtonFooter(
                                        title = TL.General.CREATE.getTranslationFlow().merge(TL.ObjectTypeTitle.OBJECT_MARKER.getTranslationFlow()),
                                        icon = { FormationIcons.TrackedObject.icon },
                                        value = Unit,
                                        clickHandlers = listOf(
                                            activeObjectStore.focusThisObject,
                                            activeObjectStore.hideActiveObjectMarker,
                                        ),
                                        addOrReplaceRoute = mapOf("add" to ObjectType.ObjectMarker.name),
                                        analyticsEventProvider = AnalyticsCategory.CreateObjectFromTrackingEvent.click(),
                                    )
                                }
                            }
                            if (!scannedCodeStore.current.extOrIntObjIdOrActionId.isNullOrEmpty()) {
                                twColOf {
                                    if (geoPositionStore.current == null) {
                                        twRowOfNoGap("items-center justify-center gap-4") {
                                            p("text-sm font-light break-words px-2") {
                                                translate(TL.CodeScanning.LOCATION_PERMISSION_QUESTION)
                                            }
                                            twIconMedium(icon = FormationIcons.ArrowRight)
                                            twColOfNoGap("items-center justify-center p-2.5 gap-1 rounded-xl") permissionButton@{
                                                className(
                                                    geoPositionStore.data.map { geoPosition ->
                                                        if (geoPosition == null) "cursor-pointer hover:bg-gray-100" else "cursor-default bg-green-50 text-green-700"
                                                    },
                                                )
                                                geoPositionStore.data.render { geoPosition ->
                                                    if (geoPosition == null) {
                                                        twIconCustomSize(FormationIcons.LocationAccess, "48px")

                                                        p(" text-center text-xs") {
                                                            translate(TL.CodeScanning.ALLOW_LOCATION_PERMISSION)
                                                        }

                                                        this@permissionButton.clicks handledBy permissionsService.requestLocationPermission

                                                    } else {
                                                        twIconCustomSize(FormationUIIcons.Check, "48px")

                                                        p(" text-center text-xs") {
                                                            translate(TL.PermissionPromptTexts.LOCATION_PERMISSION_GRANTED)
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    geoPositionStore.data.render { geoPosition ->
                                        twPrimaryButton(
                                            text = TL.CodeScanning.UPDATE_LOCATION_WITH_MY_POSITION,
                                            icon = FormationIcons.Location,
                                            disabledFlow = geoPositionStore.data.map { geoPosition -> geoPosition == null },
                                            analyticsEventProvider = geoPosition?.let {
                                                AnalyticsCategory.QuickUpdateTrackedObjectLocation.click(
                                                    location = geoPosition.latLon(),
                                                    target = scannedCodeStore.current.extOrIntObjIdOrActionId,
                                                )
                                            },
                                        ) {
                                            clicks handledBy scannedCodeStore.updateLocation
                                        }
                                    }
                                }
                            }
                        }

                        ObjectType.Task -> {
                            twColOf {
                                twTaskAssignmentButton()
                                twRowOf("flex-wrap gap-2") {
                                    twTaskStatusButton()
                                    twTaskDueDateButton()
                                }
                            }
                        }

                        ObjectType.Event -> {
                            twColOf {
                                twColOf {
                                    meetingAttendeesButton()
                                }
                                twRowOf("flex-wrap gap-2") {
                                    twMeetingInvitationStatusButton()
                                    twMeetingDateTimeButton()
                                }
                            }
                        }

                        ObjectType.Building, ObjectType.GeoFence -> {
                            nestedObjectsButton()
                        }

                        ObjectType.GeneralMarker -> {
                            geoReferencedConnectableObject.data.render { geoReferencedConnectableObject ->
                                if (geoReferencedConnectableObject != null) {
                                    val currentObj = activeObjectStore.current
                                    // check if any connector is free, check works only as long as there is only one connection per connector
                                    if ((geoReferencedConnectableObject.connections?.size ?: 0)
                                        < (geoReferencedConnectableObject.original.connectors?.size ?: 0)
                                    ) {
                                        separationLine(title = flowOf("New Connection"))
                                        selectFromConnector(currentObj)
                                        selectToConnector()
                                        connectorConnectButton()
                                    }
                                    connectionsList(currentObj)
                                }
                            }
                        }

                        ObjectType.Zone -> {
                            nestedObjectsButton()
                            customDataZoneType.render { zoneType ->
                                if (zoneType != null) {
                                    when (zoneType) {
                                        "containers", "Container", "coils", "Coil" -> capacityInfo()
                                        "machine" -> statusInfo()
                                    }
                                }
                            }

                            // TODO turn custom data screen into modal
                            customData.render { dataFlow ->
                                if (dataFlow.isNotEmpty()) {
                                    selectorButton {
                                        selectorContent {
                                            icon({ margins { horizontal { smaller } }; size { normal } }) { fromTheme { FormationIcons.API.icon } }
                                            ellipseText { translate(TL.CardCustomData.BUTTON_TITLE) }
                                        }
                                        clicks.map {
                                            mapOf(
                                                "show" to "objectData",
                                            )
                                        } handledBy routerStore.addOrReplaceRoute
                                    }
                                }
                            }
                        }

//                        ObjectType.POI,
//                        ObjectType.UserMarker,
//                        ObjectType.Floor,
//                        ObjectType.TransientMarker,
//                        ObjectType.CurrentUserMarker,
//                        ObjectType.Unit,
//                        ObjectType.Area,
//                        ObjectType.Cluster,
//                        ObjectType.Notification,
//                        ObjectType.HistoryEntry -> {}

                        else -> {}
                    }
                }
            }
        }
    }
}

fun RenderContext.breadCrumbs(obj: GeoObjectDetails? = null) {
    withKoin {
        val objectAndUserHandler: ObjectAndUserHandler = get()
        val breadcrumbsCache: BreadcrumbsCache = get()
        val lastObject = if (breadcrumbsCache.lastObjectIsChildOf(obj?.id)) {
            breadcrumbsCache.lastObject
        } else null

        // Bread crumbs
        breadcrumbsCache.getBreadCrumbs(obj).render { parents ->
            if (parents.isNotNullOrEmpty() || lastObject != null) {
                div("flex flex-row items-center w-full px-2 py-1 overflow-hidden opacity-50 hover:opacity-100") {
                    className(
                        when {
                            parents.isNotNullOrEmpty() && lastObject != null -> "justify-between"
                            parents.isNotNullOrEmpty() -> "justify-start"
                            lastObject != null -> "justify-start"
                            else -> "justify-between"
                        },
                    )

                    // left aligned parent objects
                    if (parents.isNotNullOrEmpty()) {
                        div("flex flex-row items-center overflow-hidden min-w-0") {
                            parents.forEachIndexed { index, parent ->
                                if (index > 0) {
                                    twIconSmall(icon = FormationUIIcons.ChevronRight)
                                }
                                div("flex items-center gap-1 px-2 py-1 rounded-xl hover:underline cursor-pointer overflow-hidden min-w-0 max-w-full") { //hover:bg-gray-100
                                    parent.toMapMarkerResult().getSvgIconOptions(parent.objectType).copy(size = MarkerSize.XXS).let { iconOpts ->
                                        div("flex-shrink-0") { renderSvgIcon(iconOpts) }
                                    }
                                    p("text-xs font-light truncate min-w-0") {
                                        +parent.title
                                    }
                                    clicks handledBy {
                                        objectAndUserHandler.updateAndLocateActiveObjectByInternalId(parent.id)
                                    }
                                }.twTooltip {
                                    p("text-xs font-bold") {
                                        +parent.title
                                    }
                                }
                            }
                            if (parents.isNotNullOrEmpty()) {
                                twIconSmall(icon = FormationUIIcons.ChevronRight)
                            }
                        }
                    }

                    // this object
                    twRowOfNoGap("items-center gap-1 px-1") {
//                        obj?.toMapMarkerResult()?.getSvgIconOptions(obj.objectType)?.copy(size = MarkerSize.XXS)?.let { iconOpts ->
//                            div("flex-shrink-0") { renderSvgIcon(iconOpts) }
//                        }
                        p("text-xs font-bold") { +"..." }
//                        p("text-xs font-bold underline italic") {
//                            translate(TL.CardNestedObjects.CURRENT)
//                        }
                    }

                    // right aligned child objects
                    lastObject?.let { lastObject ->
                        div("flex flex-row items-center overflow-hidden min-w-0") {
                            twIconSmall(icon = FormationUIIcons.ChevronRight)
                            div("flex items-center gap-1 px-2 py-1 rounded-xl hover:underline cursor-pointer overflow-hidden min-w-0 max-w-full") { //hover:bg-gray-100
                                lastObject.toMapMarkerResult().getSvgIconOptions(lastObject.objectType).copy(size = MarkerSize.XXS).let { iconOpts ->
                                    div("flex-shrink-0") { renderSvgIcon(iconOpts) }
                                }
                                p("text-xs font-light truncate min-w-0") {
                                    +lastObject.title
                                }
                                clicks handledBy {
                                    objectAndUserHandler.updateAndLocateActiveObjectByInternalId(lastObject.id)
                                }
                            }.twTooltip {
                                p("text-xs font-bold") {
                                    +lastObject.title
                                }
                            }
                        }
                    }
                }
//                separationLine("my-1")
            }
        }
    }
}

fun RenderContext.nestedObjectsButton() {
    withKoin {
        val routerStore: RouterStore = get()
        val nestedObjectsResultsStore: NestedObjectsResultsStore = get()
        val activeObjectStore: ActiveObjectStore = get()

        // NestedObjects
        nestedObjectsResultsStore.data.render { nestedObjectData ->
            if (nestedObjectData.fetched.isNotEmpty()) {
                div(
                    "flex flex-row items-center w-full justify-between p-2 cursor-pointer " +
                        "rounded-xl overflow-hidden hover:bg-gray-100 text-gray-400 hover:text-gray-600",
                ) {
                    div("p-2 text-formationBlack") {
                        twIconMedium(FormationIcons.Boxes)
                    }
                    titleSubtitleStack("text-formationBlack") {
                        // display total number of attendees
                        twEllipseText("font-bold text-sm") {
                            TL.CardNestedObjects.BUTTON_TITLE.getTranslationFlow(
                                mapOf(
                                    "case" to activeObjectStore.current.objectType.getCase(),
                                    "objectType" to activeObjectStore.current.objectType.getName(),
                                ),
                            )
                                .merge(flowOf("(${max(nestedObjectData.total ?: 0, nestedObjectData.fetched.size)})"))
                                .renderText()
                        }
                        // display breakdown of objectTypes
                        twRowOfNoGap("flex-grow-0 flex-shrink-0 items-center gap-1 justify-even text-xs overflow-x-hidden") {
                            nestedObjectData.fetched.groupBy { it.hit.objectType }.forEach { (objectType, objects) ->
                                iconNumber(objects.size, icon = objectType.getIcon())
                            }
                            if (nestedObjectData.fetched.size < (nestedObjectData.total ?: 0)) {
                                p { +"+..." }
                            }
                        }
                    }
                    // display icons of first 5 objects
                    nestedObjectData.fetched.take(5).forEach { obj ->
                        obj.hit.toMapMarkerResult().getSvgIconOptions(obj.hit.objectType).copy(size = MarkerSize.XXS).let { iconOpts ->
                            div("flex-shrink-0") { renderSvgIcon(iconOpts) }
                        }
                    }
                    div("pr-2") {
                        twIconMedium(FormationUIIcons.Select)
                    }
                    clicks handledBy {
                        routerStore.addOrReplaceRoute(mapOf("show" to "nestedObjects"))
                    }
                }
            }
        }
    }
}

fun RenderContext.infoCardButtons() {
//    twRowOfNoGap("gap-1") {
//    refreshObjectButton()
    closeCardButton()
//    }
}

fun RenderContext.secondaryMenuButton() {

    val routerStore: RouterStore by koinCtx.inject()

    twMediumIconButtonNeutralRounded(icon = FormationIcons.DotMenu) {
        clicks handledBy {
            routerStore.addOrReplaceRoute(mapOf("show" to "menu"))
        }
    }.twSimpleTextTooltip(TL.CardSecondaryMenu.MORE, positioning = PlacementValues.bottom)
}

fun RenderContext.closeCardButton() {
    val activeObjectStore: ActiveObjectStore by koinCtx.inject()
    twLargeIconButtonNeutralRounded(icon = FormationUIIcons.Close) {
        clicks handledBy activeObjectStore.resetActiveObjectAndUserToMap
    }.twSimpleTextTooltip(title = TL.General.CLOSE, positioning = PlacementValues.left)
}

fun RenderContext.refreshObjectButton() {

    val objectAndUserHandler: ObjectAndUserHandler by koinCtx.inject()

    button {
        flexBox(
            {
                alignItems { center }
                justifyContent { center }
                width { "30px" }
                height { "30px" }
                radius { full }
                color { primary.main }
                padding { small }
                hover {
                    color { secondary.main }
                    background {
//                    color { makeRGBA(primary.main, 0.1) }
                        color { primary.main }
                    }
                }
            },
        ) {
            objectAndUserHandler.refreshObjectTracker.data.render { loading ->
                if (loading) {
                    dotSpinner(pixelSize = 4)
                } else {
                    icon { fromTheme { FormationIcons.Update.icon } }
                }
            }
            clicks handledBy objectAndUserHandler.refreshObjectById
        }
    }
}

fun RenderContext.colorDot(color: String) {
    div("flex grow-0 shrink-0 basis-2.5 w-2.5 h-2.5 rounded-full mx-1") {
        inlineStyle("--dotColor: $color;")
        className("bg-[var(--dotColor)]")
    }
}
